import { Box } from '@mui/material';
import { Typography, colors, Button } from '@sweep-io/sweep-design';
import { Database, Refresh } from '@sweep-io/sweep-design/dist/icons';
import { revertToLatestAssignmentGroupInServer } from '../../../../reducers/assignmentGroupsPageReducer';
import {
  ActionableDataTable,
  ActionableDataTableActionTypes,
} from '../../../common/table/ActionableDataTable';
import { StyledSwitch } from '../../../common/StyledSwitch';
import { useCallback, useEffect, useState } from 'react';
import { useAssignmentsApiWithReducer } from '../useAssignmentsApiWithReducer';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { useDispatch } from 'react-redux';
import StyledTooltip from '../../../common/StyledTooltip';
import useCheckPermissionsForCurrentCrmOrg from '../../../common/permissions/useCurrentCrmOrgPermissions';
import {
  DataTableBaseColumn,
  DataTableRow,
  DataTableVariant,
} from '../../../common/table/TableTypes';
import { AssignmentGroup, AssignmentGroupMember } from '../../../../reducers/assignmentGroupTypes';
import { IntegerPicker } from './IntegerPicker';
import { Percentage } from '../../../common/Percentage';
import { Permission } from '@server/permissions';
import { telemetry } from '../../../../telemetry';
import { TableFilteredEmptyState } from '../../../common/TableFilteredEmptyState';
import { TableActionableEmptyState } from '../../../common/table/TableActionableEmptyState';
import { DataTableId } from '../../../common/table/utils';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';

const TABLE_VARIANT = DataTableVariant.medium;

const getColumns = (showLimit?: boolean, showCredits?: boolean): DataTableBaseColumn[] => [
  {
    field: 'active',
    headerName: 'Active',
    minWidth: '80px',
    width: '80px',
  },
  {
    field: 'name',
    headerName: 'Name',
    width: '300px',
  },
  {
    field: 'limit',
    width: '200px',
    headerName: 'Limit',
    hidden: !showLimit,
  },
  {
    field: 'weight',
    headerName: 'Weight distribution',
  },
  {
    field: 'credits',
    headerName: 'Credits',
    hidden: !showCredits,
  },
];

interface GroupMembersTableProps {
  groupData: AssignmentGroup;
  filterFunc: (member: AssignmentGroupMember) => boolean;
  onClearFilters: () => void;
  onAddMember: () => void;
}

export const GroupMembersTable = ({
  groupData,
  filterFunc,
  onClearFilters,
  onAddMember,
}: GroupMembersTableProps) => {
  const {
    optimisticSaveWithDebounceAssignmentGroupData,
    deleteAssignmentGroupMemberAtIdx,
    patchAssignmentGroupMemberActiveStatusAtIdx,
    patchAssignmentGroupMemberWeightAtIdx,
    patchAssignmentGroupMemberLimitAtIdx,
    patchAssignmentGroupMemberCredits,
  } = useAssignmentsApiWithReducer();
  const { addNotification } = useSweepNotifications();
  const dispatch = useDispatch();
  const [localMembers, setLocalMembers] = useState(groupData.members);
  const permissionString: Permission[] = ['edit:assignment-groups'];
  const [isAllowedBtn] = useCheckPermissionsForCurrentCrmOrg(permissionString);
  const { creditQueue: isCreditQueueEnabled } = useFeatureToggle();

  const [currentFocusIndex, setCurrentFocusIndex] = useState<number | null>(null);

  useEffect(() => {
    setLocalMembers(groupData.members);
  }, [groupData.members]);

  const withErrorNotificationAndRevert = useCallback(
    async (fn: Function, errorMessage = 'Error Saving Assignment Group') => {
      try {
        await fn();
      } catch (e) {
        telemetry.captureError(e);
        addNotification({
          message: errorMessage,
          variant: SweepNotificationVariant.Error,
        });
        dispatch(revertToLatestAssignmentGroupInServer());
      }
    },
    [addNotification, dispatch],
  );

  const totalEnabledWeights = localMembers?.reduce((acc, curr) => {
    if (curr.membershipActive) {
      acc += curr.weight;
    }
    return acc;
  }, 0);

  // Check if any member has credits (for "Up Next" vs "Next after queue" display)
  const hasAnyCredits = localMembers?.some((member) => (member.creditCount ?? 0) > 0) ?? false;

  const rows =
    localMembers?.map((member, index) => {
      const {
        userId: id,
        name,
        weight,
        membershipActive,
        userActive,
        limitValue: limit,
        creditCount = 0,
      } = member;
      const disabled = !userActive || !isAllowedBtn;
      const color = disabled ? colors.grey[500] : colors.grey[900];
      const isUpNext = id === groupData.nextMemberId;

      const tooltip = disabled
        ? !isAllowedBtn
          ? 'To modify the settings of this assignment group, please contact your admin.'
          : 'Reactivate this user in Salesforce to enable actions within group'
        : '';

      return {
        id,
        index,
        name: (
          <StyledTooltip title={tooltip}>
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                color,
              }}
            >
              <Typography variant="body" whiteSpace="nowrap">
                {name}
              </Typography>
              {isUpNext && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    '& span': { color: `${colors.grey[500]} !important` },
                  }}
                >
                  <Refresh size="small" color={colors.grey[500]} />
                  <Typography variant="caption">
                    {hasAnyCredits ? 'Next after queue' : 'Up next'}
                  </Typography>
                </Box>
              )}
            </Box>
          </StyledTooltip>
        ),
        active: (
          <StyledTooltip title={tooltip}>
            <Box>
              <StyledSwitch
                disabled={disabled}
                onChange={(e, checked) =>
                  withErrorNotificationAndRevert(() =>
                    patchAssignmentGroupMemberActiveStatusAtIdx(groupData, index, checked),
                  )
                }
                checked={membershipActive}
              />
            </Box>
          </StyledTooltip>
        ),
        weight: (
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <Box className="show-on-hover">
              <IntegerPicker
                disabled={disabled}
                value={weight}
                onChange={(weight) => {
                  const newWeight = weight === null ? 1 : weight;
                  withErrorNotificationAndRevert(() =>
                    patchAssignmentGroupMemberWeightAtIdx(groupData, index, newWeight),
                  );
                }}
                min={1}
                onFocus={() => {
                  setCurrentFocusIndex(index);
                }}
                onBlur={() => {
                  setCurrentFocusIndex(null);
                }}
              />
            </Box>
            {membershipActive && (
              <Box flex={1} pl={1} maxWidth="200px">
                <Percentage numerator={weight} denominator={totalEnabledWeights as number} active />
              </Box>
            )}
          </Box>
        ),
        limit: (
          <Box display="flex" position="relative">
            {!Boolean(limit) && (
              <Box
                className="hide-on-hover"
                sx={{ position: 'absolute', top: '10px', left: '14px', zIndex: 1 }}
              >
                N/A
              </Box>
            )}
            <Box className="show-on-hover" display="flex" gap={'12px'} alignItems="center">
              <IntegerPicker
                disabled={disabled}
                value={limit ?? null}
                onChange={(limit) => {
                  withErrorNotificationAndRevert(() =>
                    patchAssignmentGroupMemberLimitAtIdx(groupData, index, limit ?? null),
                  );
                }}
                min={0}
                zeroAsEmpty
                onFocus={() => {
                  setCurrentFocusIndex(index);
                }}
                onBlur={() => {
                  setCurrentFocusIndex(null);
                }}
              />

              {Boolean(limit) && (
                <Box
                  sx={{
                    '.MuiButtonBase-root': {
                      textAlign: 'left',
                    },
                  }}
                >
                  <Button
                    size="medium"
                    variant="flat-grey"
                    onClick={() =>
                      withErrorNotificationAndRevert(() =>
                        patchAssignmentGroupMemberLimitAtIdx(groupData, index, null),
                      )
                    }
                  >
                    Remove limit
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        ),
        credits: (
          <Box display="flex" alignItems="center" gap={1}>
            {creditCount === 0 && (
              <>
                <Box sx={{ '& span': { color: `${colors.grey[500]} !important` } }}>
                  <Typography variant="body">No credits</Typography>
                </Box>
                <Box className="show-on-hover">
                  <Button
                    size="small"
                    variant="flat-grey"
                    disabled={disabled}
                    onClick={() =>
                      withErrorNotificationAndRevert(() =>
                        patchAssignmentGroupMemberCredits(groupData.id, id, 1),
                      )
                    }
                  >
                    + Add
                  </Button>
                </Box>
              </>
            )}
            {creditCount > 0 && (
              <Box display="flex" position="relative">
                <Box
                  className="hide-on-hover"
                  sx={{ position: 'absolute', top: '10px', left: 0, zIndex: 1 }}
                  display="flex"
                  gap={2.5}
                  alignItems="center"
                >
                  <Database />
                  <Typography variant="body">{creditCount}</Typography>
                </Box>
                <Box
                  className="show-on-hover credits-input"
                  display="flex"
                  gap={1}
                  alignItems="center"
                  mt={'2px'}
                >
                  <Database />
                  <IntegerPicker
                    disabled={disabled}
                    value={creditCount}
                    onChange={(credits) => {
                      const newCredits = credits ?? 0;
                      withErrorNotificationAndRevert(() =>
                        patchAssignmentGroupMemberCredits(groupData.id, id, newCredits),
                      );
                    }}
                    min={0}
                    zeroAsEmpty
                    isRestrictToArrows
                    useImmediateUpdate
                    onFocus={() => {
                      setCurrentFocusIndex(index);
                    }}
                    onBlur={() => {
                      setCurrentFocusIndex(null);
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ),
        data: member,
      };
    }) || [];

  const showLimit = Boolean(localMembers.find((member) => member.limitValue));

  const actions = !isAllowedBtn
    ? []
    : [
        {
          type: ActionableDataTableActionTypes.DELETE,
          confirm: true,
          confirmButtonOkLabel: 'Delete',
          confirmButtonCancelLabel: 'Cancel',
          confirmTitle: (id: string, row: DataTableRow) =>
            row.data.userActive
              ? `Remove group member "${row.data.name}" from "${groupData.name}" group?`
              : `Remove non active member Salesforce user?`,
          confirmSubtitle: (id: string, row: DataTableRow) =>
            row.data.userActive
              ? `Removing "${row.data.name}" from the group will adjust the weight distribution among the remaining members.`
              : 'Please note that once members who are not active in Salesforce are removed from the assignment group, they will not ' +
                'be able to be reassigned to any group until they are re-activated in Salesforce.',
        },
      ];

  const columns = getColumns(showLimit, isCreditQueueEnabled);
  const filteredLocalMembers = localMembers.filter(filterFunc);
  const idsToDisplay = filteredLocalMembers.map((member) => member.userId);
  const rowsToDisplay = rows.filter((row) => idsToDisplay.includes(row.id));
  let _sx = {
    '.MuiTableCell-root': {
      '.MuiTypography-root': {
        overflow: 'visible',
      },
    },
    '.MuiTableRow-root:hover': {
      '.MuiOutlinedInput-notchedOutline': {
        borderColor: colors.blue[500],
      },
    },
  };
  if (currentFocusIndex !== null) {
    _sx = {
      ..._sx,
      [`tbody .MuiTableRow-root:nth-child(${currentFocusIndex + 1})`]: {
        backgroundColor: '#F8F9FA',
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: colors.blue[500],
        },
        '.hide-on-hover': {
          display: 'none',
        },
        '.credits-input': {
          '.MuiInputBase-input': { color: 'inherit' },
          svg: { opacity: 1 },
        },
      },
    };
  }

  const allowReorder = isAllowedBtn && rowsToDisplay.length === rows.length;

  const onRowOrderChange = useCallback(
    ({ sourceIndex, destinationIndex }: { sourceIndex: number; destinationIndex: number }) => {
      if (!localMembers || !groupData.members) {
        return;
      }

      let newMembers = [...localMembers];

      delete newMembers[sourceIndex];
      if (sourceIndex < destinationIndex) {
        destinationIndex++;
      }
      newMembers.splice(destinationIndex, 0, localMembers[sourceIndex]);
      newMembers = newMembers.filter((member) => member);

      setLocalMembers(newMembers);
      withErrorNotificationAndRevert(() =>
        optimisticSaveWithDebounceAssignmentGroupData({
          ...groupData,
          members: newMembers,
        }),
      );
    },
    [
      groupData,
      localMembers,
      optimisticSaveWithDebounceAssignmentGroupData,
      withErrorNotificationAndRevert,
    ],
  );

  return (
    <ActionableDataTable
      tableId={DataTableId.AssignmentGroupMembersTable}
      allowRowReorder={allowReorder}
      columns={columns}
      rows={rowsToDisplay}
      sx={_sx}
      {...(allowReorder
        ? {
            onRowOrderChange,
          }
        : {})}
      actions={actions}
      onActionClick={({ actionType, row }) => {
        switch (actionType) {
          case ActionableDataTableActionTypes.DELETE:
            withErrorNotificationAndRevert(
              () => deleteAssignmentGroupMemberAtIdx(groupData, row.index),
              'Error deleting group',
            );
            break;
        }
      }}
      sxRowFunction={(isDragging: boolean) => ({
        '& .show-on-hover': {
          button: { opacity: 0 },
          '.MuiOutlinedInput-root fieldset': { borderColor: 'transparent' },
          '.MuiDivider-root': { opacity: 0 },
        },
        '& .credits-input': {
          '.MuiInputBase-input': { color: 'transparent' },
          svg: { opacity: 0 },
        },
        '&:hover .hide-on-hover': {
          display: 'none',
        },
        '&:hover .show-on-hover': {
          button: { opacity: isDragging ? 0 : 'initial' },
          '.MuiDivider-root': { opacity: 1 },
        },
        '&:hover .credits-input': {
          '.MuiInputBase-input': { color: 'inherit' },
          svg: { opacity: 1 },
        },
      })}
      tableEmptyStateJsx={
        groupData.members.length === 0 ? (
          <TableActionableEmptyState
            title="Add members to start routing records"
            buttonLabel="Add Member"
            onButtonClick={onAddMember}
          />
        ) : (
          <TableFilteredEmptyState clearFilters={onClearFilters} isFiltered={true} />
        )
      }
      variant={TABLE_VARIANT}
    />
  );
};
